import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import React, { useContext, useEffect, useState } from 'react';
import Header from './components/Header/Header';
import Sidebar from './components/Sidebar/Sidebar';
import SupportTicketForm from './components/SupportTicketForm/SupportTicketForm';
import { Alert, Button, message } from 'antd';
import axios from 'axios';
import { GlobalContext } from './Context';
import { useIsAuthenticated } from './utils/auth';

// `children` remains supported so existing per-Route usage keeps working;
// when App.tsx uses the nested <Route element={<PrivateLayout />}> pattern,
// `children` is undefined and the <Outlet /> renders the matched child instead.
const PrivateLayout = ({ children }: { children?: React.ReactNode }) => {
  const { pathname } = useLocation();

  // Reactive auth — re-renders on login/logout across tabs and within same tab.
  const hasToken = useIsAuthenticated();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const { setPlanDetails, planDetails }: any = useContext(GlobalContext);

  /// state to store the days remaining for the downgrade & cancellation
  const [daysRemainingForDowngrade, setDaysRemainingForDowngrade] = useState(-1);
  const [downgradePlanName, setDowngradePlanName] = useState('');
  const [currentPlanName, setCurrentPlanName] = useState('');
  const [daysRemainingForCancellation, setDaysRemainingForCancellation] = useState(-1);

  /// loader to disable the button
  const [loading, setLoading] = useState(false);

  const userId = localStorage.getItem('userId');

  const navigate = useNavigate();
  useEffect(() => {
    if (!hasToken) {
      navigate('/login');
      return;
    }

    axios
      .get(`${process.env.REACT_APP_SERVER_URL}/plan-details?userId=${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        }
      })
      .then((res) => {
        /// if the user has cancelled the subscription then show the alert
        const currentPlanName: string = res.data?.name;
        const cancellationDateString: number = res.data?.cancellationDate;
        const downgradeDateString: number = res.data?.downgradeScheduleDate;

        /// process the date and calculate the days remaining
        if (downgradeDateString) {
          // Split the downgradeDateString into day, month, and year (DD/MM/YYYY format)
          // const [day, month, year] = downgradeDateString.split('/').map((num: string) => parseInt(num, 10));

          // Create the downgrade date in UTC by using Date.UTC(year, month, day)
          const downgradeDateUTC = new Date(downgradeDateString * 1000); // Months are 0-indexed
          downgradeDateUTC.setUTCHours(0, 0, 0, 0);

          // Get the current date in UTC and set time to 00:00:00 for accurate day-only comparison
          const currentDateUTC = new Date();
          currentDateUTC.setUTCHours(0, 0, 0, 0); // Set current time to midnight UTC

          // Calculate the time remaining between the downgrade date and the current date in UTC
          const timeRemaining = downgradeDateUTC.getTime() - currentDateUTC.getTime();

          // Calculate the number of days remaining
          setDaysRemainingForDowngrade(
            Math.floor(timeRemaining / (1000 * 60 * 60 * 24)) // Dividing by the number of milliseconds in a day
          );

          setDowngradePlanName(res.data?.downgradePlan);
        }

        if (cancellationDateString) {
          // Split the cancellationDate string into day, month, and year (DD/MM/YYYY format)
          // const [day, month, year]: any = cancellationDateString.split('/');

          // Create the cancellation date in UTC by using Date.UTC(year, month, day)
          const cancellationDateUTC: any = new Date(cancellationDateString * 1000); // Months are 0-indexed in JavaScript
          cancellationDateUTC.setUTCHours(0, 0, 0, 0);

          // If the date is invalid, return an error message
          // if (isNaN(cancellationDateUTC)) {
          //   console.error('Invalid cancellation date format');
          //   return;
          // }

          // Get the current date in UTC and set time to 00:00:00 for accurate day-only comparison
          const currentDateUTC: any = new Date();
          currentDateUTC.setUTCHours(0, 0, 0, 0); // Set current time to midnight UTC for comparison

          // Calculate the difference in time between the cancellation date and the current date in UTC
          const timeDifference = cancellationDateUTC.getTime() - currentDateUTC.getTime();

          // Calculate the number of days remaining
          const daysRemaining = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

          setDaysRemainingForCancellation(daysRemaining);
        }

        setCurrentPlanName(currentPlanName);
        setPlanDetails({
          ...res.data,
          maxCount: res.data.name === 'Free' ? 2 : res.data.name === 'Basic' ? 4 : 10
        });
      })

      .catch((err) => {
        if (userId) {
          message.error({ content: 'Unable to fetch plan details. Please, try again later.', key: 'error-plan-fetch' });
        }
      });
    // Refresh plan details on every navigation so downgrade/cancellation
    // alerts stay in sync — this replaces the per-nav remount that used to
    // trigger this effect before we moved to a persistent PrivateLayout.
  }, [pathname, hasToken]);

  /// function to reactivate the plan if user has cancelled the subscription
  async function reactivatePlan() {
    try {
      setLoading(true);
      await axios.get(`${process.env.REACT_APP_SERVER_URL}/reactivate-subscription?userId=${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      setLoading(false);
      message.success({ content: 'Plan resumed successfully.', key: 'plan-reactive' }).then(() => {
        window.location.reload();
      });
    } catch (err) {
      console.log(err);
      setLoading(true);
      message.error({ content: 'Unable to resume plan. Please, try again later.', key: 'plan-reactivation-error' });
    }
  }

  /// function to resume the plan to current plan if user has downgraded the subscription
  async function resumePlan() {
    try {
      setLoading(true);
      await axios.get(`${process.env.REACT_APP_SERVER_URL}/resume-subscription?userId=${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      setLoading(false);
      message.success({ content: 'Plan resumed successfully.', key: 'plan-reactive' }).then(() => {
        window.location.reload();
      });
    } catch (err) {
      console.log(err);
      setLoading(true);
      message.error({ content: 'Unable to resume plan. Please, try again later.', key: 'plan-reactivation-error' });
    }
  }

  if (hasToken) {
    return (
      <>
        <Header onMenuToggle={() => setMobileSidebarOpen(p => !p)} />
        <div className='main-wrapper'>
          {mobileSidebarOpen && (
            <div
              className='sidebar-overlay'
              onClick={() => setMobileSidebarOpen(false)}
            />
          )}
          <Sidebar mobileOpen={mobileSidebarOpen} onMobileClose={() => setMobileSidebarOpen(false)} />

          <div className='main-wrapper-content'>
            {/* if user plan is going to expire then show this alert */}
            {!pathname.includes('/createblog') && !pathname.includes('/blog-edit') && (
              <div 
                className='subscription-alert'
                style={planDetails?.isTrial && daysRemainingForCancellation < 0 ? { background: '#ff7c02' } : {}}
              >
              {/* If user plan is going to be downgraded, show this alert */}
              {daysRemainingForDowngrade >= 0 && (
                <Alert
                  type='error'
                  message={
                    <span>
                      You will be moved to <b style={{ fontWeight: 600 }}>{downgradePlanName}</b>
                      {' plan within '}
                      <b style={{ fontWeight: 600 }}>
                        {daysRemainingForDowngrade > 1 ? `${daysRemainingForDowngrade} days` : '24 hrs'}
                      </b>
                    </span>
                  }
                  banner
                  action={
                    <Button type='primary' size='small' onClick={resumePlan} disabled={loading} loading={loading}>
                      Resume Plan
                    </Button>
                  }
                  className='subscription-alert-message'
                />
              )}

              {/* If user is currently on an active trial, show bold global orange trial strip */}
              {planDetails?.isTrial && daysRemainingForCancellation < 0 && planDetails?.trialEndsAt && (
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#ff7c02', padding: '10px 0' }}>
                  <span style={{ color: '#fff', fontSize: '15px', fontWeight: 500, letterSpacing: '0.3px' }}>
                    Your free trial is activated! It will complete on <b style={{ fontWeight: 800, color: '#ffe600' }}>{new Date(planDetails.trialEndsAt * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</b>.
                  </span>
                </div>
              )}

              {/* If user plan is going to expire, show this alert */}
              {daysRemainingForCancellation >= 0 && (
                <Alert
                  type='error'
                  message={
                    <span>
                      Your <b style={{ fontWeight: 600 }}>{planDetails?.isTrial ? 'Free Trial' : `${currentPlanName} plan`}</b> will be cancelled in
                      <b style={{ fontWeight: 600 }}>
                        {daysRemainingForCancellation > 1 ? ` ${daysRemainingForCancellation} days` : ' 24 hrs'}
                      </b>
                    </span>
                  }
                  banner
                  action={
                    <Button type='primary' size='small' onClick={reactivatePlan} disabled={loading} loading={loading}>
                      {planDetails?.isTrial ? 'Resume Trial' : 'Reactivate Plan'}
                    </Button>
                  }
                  className='subscription-alert-message'
                />
              )}
            </div>
            )}

            <div className='main-content'>{children ?? <Outlet />}</div>
          </div>
        </div>
        {/* <Footer/> */}
      </>
    );
  } else {
    return <></>;
  }
};

export default PrivateLayout;
